/**
 * Room API Service
 * Manages room/bed data using local storage
 */

import LocalStorageService from '../storage/LocalStorageService';

class RoomService {
  constructor() {
    this.storageKey = 'rooms';
  }

  async getAll() {
    try {
      const rooms = LocalStorageService.get(this.storageKey) || [];
      return { success: true, data: rooms };
    } catch (error) {
      console.error('Error getting all rooms:', error);
      return { success: false, error: error.message };
    }
  }

  async getById(roomId) {
    try {
      const room = LocalStorageService.findById(this.storageKey, roomId);
      if (!room) {
        return { success: false, error: 'Room not found' };
      }
      return { success: true, data: room };
    } catch (error) {
      console.error('Error getting room by ID:', error);
      return { success: false, error: error.message };
    }
  }

  async getByFacilityId(facilityId) {
    try {
      const allRooms = LocalStorageService.get(this.storageKey) || [];
      const filtered = allRooms.filter(r => r.facilityId === facilityId);
      return { success: true, data: filtered };
    } catch (error) {
      console.error('Error getting rooms by facility:', error);
      return { success: false, error: error.message };
    }
  }

  async getByWardId(wardId) {
    try {
      const allRooms = LocalStorageService.get(this.storageKey) || [];
      const filtered = allRooms.filter(r => r.wardId === wardId);
      return { success: true, data: filtered };
    } catch (error) {
      console.error('Error getting rooms by ward:', error);
      return { success: false, error: error.message };
    }
  }

  async getAvailableRooms(facilityId) {
    try {
      const allRooms = LocalStorageService.get(this.storageKey) || [];
      const filtered = allRooms.filter(
        r => r.facilityId === facilityId && r.status === 'available' && r.availableBeds > 0
      );
      return { success: true, data: filtered };
    } catch (error) {
      console.error('Error getting available rooms:', error);
      return { success: false, error: error.message };
    }
  }

  async create(roomData) {
    try {
      const { createRoom } = require('../../data/models/Room');
      const newRoom = createRoom(roomData);
      const added = LocalStorageService.addItem(this.storageKey, newRoom);
      return { success: true, data: added };
    } catch (error) {
      console.error('Error creating room:', error);
      return { success: false, error: error.message };
    }
  }

  async update(roomId, updates) {
    try {
      const updated = LocalStorageService.updateItem(this.storageKey, roomId, {
        ...updates,
        updatedAt: new Date().toISOString()
      });
      if (!updated) {
        return { success: false, error: 'Room not found' };
      }
      return { success: true, data: updated };
    } catch (error) {
      console.error('Error updating room:', error);
      return { success: false, error: error.message };
    }
  }

  async delete(roomId) {
    try {
      const deleted = LocalStorageService.deleteItem(this.storageKey, roomId);
      if (!deleted) {
        return { success: false, error: 'Room not found' };
      }
      return { success: true };
    } catch (error) {
      console.error('Error deleting room:', error);
      return { success: false, error: error.message };
    }
  }

  async deleteByWardId(wardId) {
    try {
      const allRooms = LocalStorageService.get(this.storageKey) || [];
      const wardRooms = allRooms.filter(r => r.wardId === wardId);
      for (const room of wardRooms) {
        await this.delete(room.id);
      }
      return { success: true, deletedCount: wardRooms.length };
    } catch (error) {
      console.error('Error deleting rooms by ward:', error);
      return { success: false, error: error.message };
    }
  }

  async getOccupancySummary(facilityId) {
    try {
      const allRooms = LocalStorageService.get(this.storageKey) || [];
      const facilityRooms = facilityId
        ? allRooms.filter(r => r.facilityId === facilityId)
        : allRooms;

      const totalRooms = facilityRooms.length;
      const totalBeds = facilityRooms.reduce((sum, r) => sum + (r.totalBeds || 0), 0);
      const occupiedBeds = facilityRooms.reduce((sum, r) => sum + (r.occupiedBeds || 0), 0);
      const availableBeds = totalBeds - occupiedBeds;
      const occupancyRate = totalBeds > 0 ? ((occupiedBeds / totalBeds) * 100).toFixed(1) : 0;

      return {
        success: true,
        data: { totalRooms, totalBeds, occupiedBeds, availableBeds, occupancyRate: Number(occupancyRate) }
      };
    } catch (error) {
      console.error('Error getting occupancy summary:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new RoomService();
